import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';

export type SearchOption = { value: string; label: string; hint?: string };

/** Sin acentos ni mayúsculas, para que «jose» encuentre «José». */
const fold = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Personas de la API ({ personId, name }) como opciones del buscador. */
export const personOptions = (people: readonly { personId: string; name: string }[]): SearchOption[] => people.map((p) => ({ value: p.personId, label: p.name }));

/**
 * Buscador que reemplaza a un <select> largo (p. ej. elegir a un miembro): se escribe y va sugiriendo.
 * Lleva un <input type="hidden" name={name}> con el valor elegido, así los formularios con FormData
 * funcionan igual que con el <select>. Puede ser controlado (value + onChange) o no (defaultValue).
 */
export function SearchSelect({
  id,
  name,
  options,
  value,
  defaultValue = '',
  onChange,
  emptyLabel,
  placeholder = 'Escribe para buscar…',
  disabled,
  required,
  'aria-label': ariaLabel,
}: {
  id?: string;
  name?: string;
  options: readonly SearchOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Texto de «ninguno» (p. ej. «Por definir»). Sin él no se puede dejar vacío una vez elegido. */
  emptyLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  'aria-label'?: string;
}) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const listId = `${inputId}-sugerencias`;
  const [inner, setInner] = useState(defaultValue);
  const raw = value ?? inner;
  // Si lo elegido ya no está entre las opciones (p. ej. ya se agregó), queda vacío.
  const selected = raw && options.some((o) => o.value === raw) ? raw : '';
  const inputRef = useRef<HTMLInputElement>(null);
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? '';
  const [text, setText] = useState(() => labelOf(selected));
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  // Si el valor cambia desde fuera (o llegan las opciones), se muestra su nombre.
  useEffect(() => {
    if (!open) setText(labelOf(selected));
  }, [selected, options]);

  const matches = useMemo(() => {
    const words = fold(text.trim()).split(/\s+/).filter(Boolean);
    const typing = open && text !== labelOf(selected);
    const found = typing && words.length ? options.filter((o) => words.every((w) => fold(`${o.label} ${o.hint ?? ''}`).includes(w))) : options;
    const withEmpty = emptyLabel !== undefined && (!typing || words.length === 0) ? [{ value: '', label: emptyLabel }, ...found] : found;
    return withEmpty.slice(0, 50);
  }, [text, options, open, selected, emptyLabel]);

  function choose(next: string) {
    if (value === undefined) setInner(next);
    setText(labelOf(next));
    setOpen(false);
    if (next !== selected) onChange?.(next);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) return setOpen(true);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => Math.max(0, Math.min(matches.length - 1, i + step)));
    } else if (event.key === 'Enter' && open) {
      event.preventDefault();
      const pick = matches[active];
      if (pick) choose(pick.value);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
      setText(labelOf(selected));
    }
  }

  // form.reset() también limpia el buscador (como haría con un <select>).
  useEffect(() => {
    const form = inputRef.current?.form;
    if (!form || value !== undefined) return;
    const onReset = () => {
      setInner(defaultValue);
      setText(options.find((o) => o.value === defaultValue)?.label ?? '');
    };
    form.addEventListener('reset', onReset);
    return () => form.removeEventListener('reset', onReset);
  }, [value, defaultValue, options]);

  useEffect(() => {
    listRef.current?.children[active]?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  return (
    <div className="search-select">
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && matches[active] ? `${listId}-${active}` : undefined}
        aria-label={ariaLabel}
        autoComplete="off"
        value={text}
        placeholder={emptyLabel ?? placeholder}
        disabled={disabled}
        required={required && !selected}
        onFocus={(e) => {
          e.currentTarget.select();
          setActive(0);
          setOpen(true);
        }}
        onChange={(e) => {
          const typed = e.target.value;
          // Elegir por su clave exacta (lo usan las pruebas y quien pega un id).
          if (options.some((o) => o.value === typed) || (typed === '' && emptyLabel !== undefined && !open)) return choose(typed);
          setText(typed);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          // Al salir: si borró todo y se permite, queda vacío; si no, vuelve a lo elegido.
          if (text.trim() === '' && emptyLabel !== undefined) choose('');
          else {
            setText(labelOf(selected));
            setOpen(false);
          }
        }}
      />
      {name ? <input type="hidden" name={name} value={selected} /> : null}
      {open && !disabled ? (
        <ul className="search-select__list" id={listId} role="listbox" ref={listRef}>
          {matches.length === 0 ? <li className="search-select__empty">Nadie con ese nombre</li> : null}
          {matches.map((o, i) => (
            <li
              key={o.value || '∅'}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={o.value === selected}
              className={`search-select__option${i === active ? ' search-select__option--active' : ''}${o.value === '' ? ' muted' : ''}`}
              // mousedown (no click) para elegir antes de que el input pierda el foco.
              onMouseDown={(e) => {
                e.preventDefault();
                choose(o.value);
              }}
              onMouseEnter={() => setActive(i)}
            >
              {o.label}
              {o.hint ? <span className="small muted"> · {o.hint}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
